import { getServerTimeOffsetMs } from '@/api/client';

/**
 * Where the list is kept: a person's sealed copy (`PersonalStore`), or, in a
 * browser that keeps nothing, a list held in memory for as long as the page.
 */
export interface OutboxHolder {
  readonly userId: string;
  read<T>(name: string): Promise<T | null>;
  write(name: string, value: unknown): Promise<void>;
}

/**
 * Work saved in this browser that the server has not been told about yet
 * (.docs/023, step 4).
 *
 * Every field save goes in here first, connection or not, and is sent from
 * here. One way to save, so the online path and the offline path are the same
 * path and the retry bugs fixed in step 1 cannot come back.
 *
 * Each **operation** is one request: its retry key is made when it is saved
 * and never changes, so a reply that was lost looks to the server exactly like
 * the request it already answered. An operation may stand on another's result
 * — a post on its create — and carries `{dep}` in its path until that result is
 * known.
 *
 * Kept in the person's own sealed copy, as one list, and only ever changed
 * inside `changeOutbox`, which several tabs take turns at.
 */

/**
 * Work that the server checks one piece against another shares a lane, and
 * goes in the order it was saved. A refusal stops only its own lane. The
 * reasoning for each is in .docs/023, "Lanes".
 */
export type Lane =
  | 'stock'
  | 'demands'
  | 'flock'
  | 'other-sales'
  | 'receipts'
  | 'cash-book'
  | 'expenses'
  | 'attendance'
  | 'advances'
  | 'payroll'
  | 'personal'
  | 'reference';

/** Every kind of operation this build can send, and its lane. */
export const KINDS = {
  // Stock IN
  'inward.create': 'stock',
  'inward.edit': 'stock',
  'inward.post': 'stock',
  'inward.cancel': 'stock',
  'inward.reverse': 'stock',
  // Stock OUT
  'consumption.create': 'stock',
  'consumption.edit': 'stock',
  'consumption.post': 'stock',
  'consumption.cancel': 'stock',
  'consumption.reverse': 'stock',
  // Transfers & Returns
  'transfer.create': 'stock',
  'transfer.edit': 'stock',
  'transfer.post': 'stock',
  'transfer.cancel': 'stock',
  'transfer.reverse': 'stock',
  // Adjustments, and deciding them: an approved adjustment moves stock.
  'adjustment.create': 'stock',
  'adjustment.edit': 'stock',
  'adjustment.cancel': 'stock',
  'adjustment.approve': 'stock',
  'adjustment.reject': 'stock',
  'adjustment.post': 'stock',
  'adjustment.reverse': 'stock',
  // Stock Demands
  'demand.create': 'demands',
  'demand.cancel': 'demands',
  'demand.approve': 'demands',
  'demand.reject': 'demands',
  'demand.fulfil': 'demands',
  // The Items Manager's lists, added with no line: a code is the server's to
  // give, so each is made when it is sent, and what stands on it waits.
  'uom.create': 'reference',
  'category.create': 'reference',
  'supplier.create': 'reference',
  'sku.create': 'reference',
} as const satisfies Record<string, Lane>;

export type Kind = keyof typeof KINDS;

export type OperationState = 'waiting' | 'sending' | 'sent' | 'refused';

export interface Operation {
  /** Sent as `Idempotency-Key` on every attempt. Never changes. */
  id: string;
  kind: Kind;
  lane: Lane;
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** May hold `{dep}`, filled with the id the server gave `dependsOn`. */
  path: string;
  body?: unknown;
  /** The operation whose result this needs, if any. */
  dependsOn: string | null;
  /**
   * Every operation whose new id this names, as `{op:<id>}` in its path or
   * body — a receipt's line naming a supplier added offline, say. Absent on
   * work saved before there were any.
   */
  refs?: string[];
  /** What it is, in words, for the lists and Needs attention. */
  describes: string;
  /** The screen it was typed on, so a refusal can be shown and fixed there. */
  screen: string;
  /**
   * What the screen needs to show it, and to load it back into the form on
   * Fix it: the form as it was typed, not the request it became.
   */
  form?: unknown;
  /** When it was typed, by the server's clock as far as this browser knows it. */
  createdAt: number;
  appVersion: string;
  state: OperationState;
  attempts: number;
  /** The server's own words, when it refused. */
  lastError: string | null;
  /**
   * When it was refused, by the same clock as `createdAt`. Work in its lane
   * saved before then waits for it; work saved after goes. Absent on work
   * refused before it was kept.
   */
  refusedAt?: number;
  httpStatus: number | null;
  /** The id of what the server made, once it has been sent. */
  resultId: string | null;
  /** The server's answer, for showing its number. */
  result: unknown;
  sentAt: number | null;
  /**
   * Saved with no answer from the server: no line, or a server too slow to
   * reply. Such work is listed on Offline work, and kept there after it has
   * gone until somebody clears it.
   */
  keptOffline?: true;
  /**
   * The first operation of the batch this was saved in: a receipt, its post
   * and any supplier typed on it are one piece of work to the person who
   * saved them. Absent on work saved before there were any.
   */
  batch?: string;
}

/** What a screen hands over to be sent. */
export interface NewOperation {
  /** Its id, when a screen has to name it before it is saved — see `localId`. */
  id?: string;
  kind: Kind;
  method: Operation['method'];
  path: string;
  body?: unknown;
  /** Index into the same batch of the operation this one stands on. */
  after?: number;
  describes: string;
  screen: string;
  form?: unknown;
}

const NAME = 'outbox';

/** How long sent work is kept, so lists and projected stock can still see it. */
export const KEEP_SENT_MS = 7 * 86_400_000;

let localTurn: Promise<unknown> = Promise.resolve();

/**
 * Reads the list, lets `change` alter it, and writes it back — one tab at a
 * time, through the browser's Web Locks, so two tabs saving at once cannot
 * each write over the other's work. A browser without them takes turns
 * within this tab only.
 */
export async function changeOutbox<T>(
  store: OutboxHolder,
  change: (ops: Operation[]) => { ops: Operation[]; result: T },
): Promise<T> {
  const run = async () => {
    const ops = (await store.read<Operation[]>(NAME)) ?? [];
    const next = change(ops.map((op) => ({ ...op })));
    await store.write(NAME, next.ops);
    return next.result;
  };
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (locks) return locks.request(`poultry.outbox.${store.userId}`, run) as Promise<T>;
  const turn = localTurn.then(run, run);
  localTurn = turn.catch(() => undefined);
  return turn;
}

export async function readOutbox(store: OutboxHolder): Promise<Operation[]> {
  return (await store.read<Operation[]>(NAME)) ?? [];
}

/**
 * A batch as operations, not yet kept anywhere: ids made, each standing on
 * the one it names with `after`, all of one batch. What `enqueue` keeps, and
 * what a save sent straight to the server describes itself with.
 */
export function makeOps(batch: readonly NewOperation[]): Operation[] {
  const now = Date.now() + getServerTimeOffsetMs();
  const made: Operation[] = batch.map((input) => ({
    id: input.id ?? newOperationId(),
    kind: input.kind,
    lane: KINDS[input.kind],
    method: input.method,
    path: input.path,
    ...(input.body === undefined ? {} : { body: input.body }),
    dependsOn: null,
    describes: input.describes,
    screen: input.screen,
    ...(input.form === undefined ? {} : { form: input.form }),
    createdAt: now,
    appVersion: __APP_VERSION__,
    state: 'waiting',
    attempts: 0,
    lastError: null,
    httpStatus: null,
    resultId: null,
    result: null,
    sentAt: null,
  }));
  batch.forEach((input, i) => {
    if (input.after !== undefined) made[i]!.dependsOn = made[input.after]!.id;
    made[i]!.batch = made[0]!.id;
  });
  return made;
}

/**
 * Saves a batch of operations together — a create and its post, say — so
 * neither can be kept without the other. Returns their ids, in order.
 */
export function enqueue(store: OutboxHolder, batch: readonly NewOperation[]): Promise<string[]> {
  const made = makeOps(batch);
  return changeOutbox(store, (ops) => {
    // What has already gone is named by its real id; what has not, by a token
    // filled in when it has.
    const sent = new Map(ops.filter((op) => op.state === 'sent' && op.resultId).map((op) => [op.id, op.resultId!]));
    for (const op of made) {
      const path = tokenise(op.path, sent);
      const body = tokenise(op.body, sent);
      op.path = path.value as string;
      if (op.body !== undefined) op.body = body.value;
      const refs = new Set([...path.refs, ...body.refs]);
      if (op.dependsOn) refs.add(op.dependsOn);
      if (refs.size) op.refs = [...refs];
    }
    return { ops: [...ops, ...made], result: made.map((op) => op.id) };
  });
}

// ------------------------------------------------- naming unsent work

const LOCAL = 'local:';
const TOKEN = /\{op:([0-9a-f-]{36})\}/g;

/**
 * The id a screen gives something saved on this PC and not yet made by the
 * server — a supplier typed on a receipt line, say — so it can be chosen in a
 * form like any other. Saved work naming it waits for it to be made.
 */
export function localId(opId: string): string {
  return `${LOCAL}${opId}`;
}

/** The operation a local id stands for, or null for a real one. */
export function opOfLocalId(id: string | null | undefined): string | null {
  return id && id.startsWith(LOCAL) ? id.slice(LOCAL.length) : null;
}

/**
 * Swaps every local id in a value for a token to be filled in when sending —
 * or straight for the real id, when what it names has already gone.
 */
export function tokenise(value: unknown, sent: ReadonlyMap<string, string> = new Map()): { value: unknown; refs: string[] } {
  const refs = new Set<string>();
  const walk = (v: unknown): unknown => {
    if (typeof v === 'string') {
      const opId = opOfLocalId(v);
      if (!opId) return v;
      const real = sent.get(opId);
      if (real) return real;
      refs.add(opId);
      return `{op:${opId}}`;
    }
    if (Array.isArray(v)) return v.map(walk);
    if (v !== null && typeof v === 'object') return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, walk(x)]));
    return v;
  };
  return { value: walk(value), refs: [...refs] };
}

/** Fills every token in a value with the real id, from `ids`. */
export function fillTokens(value: unknown, ids: ReadonlyMap<string, string | null>): unknown {
  if (typeof value === 'string') return value.replace(TOKEN, (token, opId: string) => ids.get(opId) ?? token);
  if (Array.isArray(value)) return value.map((v) => fillTokens(v, ids));
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, fillTokens(v, ids)]));
  }
  return value;
}

/** Every operation this one waits for. */
export function refsOf(op: Pick<Operation, 'dependsOn' | 'refs'>): string[] {
  const refs = new Set(op.refs ?? []);
  if (op.dependsOn) refs.add(op.dependsOn);
  return [...refs];
}

/** `id`, and everything standing on it however deep, among `ops`. */
export function withEverythingOn(ops: readonly Operation[], id: string): Set<string> {
  const gone = new Set([id]);
  for (let grew = true; grew; ) {
    grew = false;
    for (const op of ops) {
      if (!gone.has(op.id) && refsOf(op).some((ref) => gone.has(ref))) {
        gone.add(op.id);
        grew = true;
      }
    }
  }
  return gone;
}

/**
 * Puts a refused operation back in its place to be sent again.
 *
 * A refusal means the server did no work (every create and post runs in one
 * transaction), and the server keeps a 4xx answer against its key for a month
 * and gives the same answer back. So it goes again under a **new** key, and
 * whatever stood on the old one is pointed at the new one, all in one change
 * (.docs/022, .docs/023). `body`/`form` replace what was typed, for Fix it.
 */
export function sendAgain(
  store: OutboxHolder,
  id: string,
  fixed?: {
    body?: unknown;
    form?: unknown;
    describes?: string;
    /**
     * Goes after everything saved since, with whatever stands on it: for a fix
     * that names something new saved just now in its own lane — an item put
     * right with a new category — which must go first.
     */
    toEnd?: boolean;
  },
): Promise<string | null> {
  return changeOutbox(store, (ops) => {
    const op = ops.find((o) => o.id === id);
    if (!op || op.state !== 'refused') return { ops, result: null };
    const newId = newOperationId();
    for (const other of ops) {
      if (other === op) continue;
      if (other.dependsOn === id) other.dependsOn = newId;
      if (other.refs?.includes(id)) {
        // What names it — by token in what is sent, by local id in the form
        // that is loaded back for Fix it — names the new key instead.
        other.refs = other.refs.map((ref) => (ref === id ? newId : ref));
        other.path = other.path.replaceAll(id, newId);
        if (other.body !== undefined) other.body = JSON.parse(JSON.stringify(other.body).replaceAll(id, newId));
        if (other.form !== undefined) other.form = JSON.parse(JSON.stringify(other.form).replaceAll(id, newId));
      }
    }
    Object.assign(op, {
      id: newId,
      state: 'waiting' as const,
      lastError: null,
      httpStatus: null,
      ...(fixed?.form === undefined ? {} : { form: fixed.form }),
      ...(fixed?.describes === undefined ? {} : { describes: fixed.describes }),
    });
    delete op.refusedAt;
    if (fixed?.body !== undefined) {
      // What the form now names, saved on this PC or already sent, as on enqueue.
      const sent = new Map(ops.filter((o) => o.state === 'sent' && o.resultId).map((o) => [o.id, o.resultId!]));
      const body = tokenise(fixed.body, sent);
      const pathRefs = [...op.path.matchAll(TOKEN)].map((m) => m[1]!);
      op.body = body.value;
      const refs = new Set([...pathRefs, ...body.refs]);
      if (op.dependsOn) refs.add(op.dependsOn);
      if (refs.size) op.refs = [...refs];
      else delete op.refs;
    }
    if (fixed?.toEnd) {
      const moving = withEverythingOn(ops, newId);
      return { ops: [...ops.filter((o) => !moving.has(o.id)), ...ops.filter((o) => moving.has(o.id))], result: newId };
    }
    return { ops, result: newId };
  });
}

/**
 * Takes work out of the queue, with everything standing on it. Only on
 * somebody's say-so, or when a screen that was still open has shown the
 * server's refusal on the form itself.
 */
export function withdraw(store: OutboxHolder, id: string): Promise<Operation[]> {
  return changeOutbox(store, (ops) => {
    // Anything standing on what is withdrawn goes too, however deep.
    const gone = withEverythingOn(ops, id);
    return { ops: ops.filter((op) => !gone.has(op.id)), result: ops.filter((op) => gone.has(op.id)) };
  });
}

/**
 * Forgets work sent more than a week ago — unless work still waiting names
 * it, and needs its id to be sent, or it was kept offline: that stays on
 * Offline work until somebody clears it.
 */
export function forgetOldSent(ops: Operation[], now: number): Operation[] {
  const needed = new Set(ops.filter((op) => op.state !== 'sent').flatMap(refsOf));
  return ops.filter(
    (op) => op.state !== 'sent' || op.keptOffline || needed.has(op.id) || (op.sentAt ?? now) > now - KEEP_SENT_MS,
  );
}

/** Notes that this work was saved without an answer from the server. */
export function markKeptOffline(store: OutboxHolder, ids: readonly string[]): Promise<void> {
  const marking = new Set(ids);
  return changeOutbox(store, (ops) => {
    for (const op of ops) if (marking.has(op.id)) op.keptOffline = true;
    return { ops, result: undefined };
  });
}

/**
 * Clears the record of work kept offline that has since gone. What is still
 * waiting, or refused, stays; so does sent work that waiting work still names.
 * The usual week's grace for sent work is kept, unmarked, for projected stock.
 */
export function clearSentOffline(store: OutboxHolder, now: number): Promise<number> {
  return changeOutbox(store, (ops) => {
    let cleared = 0;
    for (const op of ops) {
      if (op.state === 'sent' && op.keptOffline) {
        delete op.keptOffline;
        cleared += 1;
      }
    }
    return { ops: forgetOldSent(ops, now), result: cleared };
  });
}

/** A version-4 UUID: the retry key's shape is the server's to check. */
export function newOperationId(): string {
  const bytes = globalThis.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
